import { ReactNode } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import PaymentsIcon from "@mui/icons-material/Payments";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { formatInteger } from "../formatters";
import {
  BRAND_STAGES,
  StageCounts,
  findBiggestDropOff,
  formatLeadCount,
  formatOptionalPercent,
  formatOptionalSignedPercent,
  retainedPercent,
} from "./brandStagesApiMapping";

const PRIMARY = "#8E24AA";
const NEGATIVE = "#C62828";

interface BrandStagesKpiCardProps {
  label: string;
  icon: ReactNode;
  value: string;
  valueColor?: string;
  subtitle: string;
}

function BrandStagesKpiCard({ label, icon, value, valueColor = "#1a1a1a", subtitle }: BrandStagesKpiCardProps) {
  return (
    <Paper variant="outlined" sx={{ p: "18px 20px", borderColor: "#e6e6e6", borderRadius: "6px" }}>
      <Stack direction="row" alignItems="center">
        <Typography variant="caption" fontWeight={600} color={PRIMARY} sx={{ flex: 1 }}>
          {label}
        </Typography>
        {icon}
      </Stack>
      <Typography variant="h5" fontWeight={700} sx={{ mt: 1, fontSize: 30, color: valueColor }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: "#9e9e9e", fontSize: 12, display: "block", mt: 0.25 }}>
        {subtitle}
      </Typography>
    </Paper>
  );
}

interface BrandStagesKpiCardsProps {
  // Null while the all-brands Lead Funnel is loading or failed - values
  // then show "—".
  counts: StageCounts | null;
  // Brands with at least one paid lead in range; null until known.
  brandCount: number | null;
}

// Exactly three cards, not clickable (spec Section 3.2). There is no
// income card - Lead Funnel returns no income.
export default function BrandStagesKpiCards({ counts, brandCount }: BrandStagesKpiCardsProps) {
  const paid = counts ? counts[0] : null;
  const funded = counts ? counts[4] : null;
  const dropOff = counts ? findBiggestDropOff(counts) : null;

  const paidSubtitle =
    brandCount === null ? "—" : `across ${brandCount} brand${brandCount === 1 ? "" : "s"} in range`;
  const fundedSubtitle = `${counts ? formatOptionalPercent(retainedPercent(counts, 4)) : "—"} of paid leads`;
  const dropOffSubtitle = dropOff
    ? `${BRAND_STAGES[0].name} → ${BRAND_STAGES[dropOff.index].name} · ${formatOptionalSignedPercent(
        dropOff.change
      )}`
    : counts
      ? "No paid leads in range"
      : "—";

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 2 }}>
      <BrandStagesKpiCard
        label="PAID LEADS"
        icon={<VerifiedIcon sx={{ color: "#2E7D32" }} />}
        value={paid === null ? "—" : formatInteger(paid)}
        subtitle={paidSubtitle}
      />
      <BrandStagesKpiCard
        label="FUNDED"
        icon={<PaymentsIcon sx={{ color: "#1B5E20" }} />}
        value={funded === null ? "—" : formatInteger(funded)}
        subtitle={fundedSubtitle}
      />
      <BrandStagesKpiCard
        label="BIGGEST DROP-OFF"
        icon={<TrendingDownIcon sx={{ color: NEGATIVE }} />}
        value={dropOff ? formatLeadCount(dropOff.lost) : "—"}
        valueColor={dropOff ? NEGATIVE : "#1a1a1a"}
        subtitle={dropOffSubtitle}
      />
    </Box>
  );
}
